/* global Word */

import type { LayoutConfig, TemplateProfile } from "../../../templates/types";
import type { ExhibitInsertionStrategy } from "../types";
import { TableBuilder } from "../../builders/TableBuilder";
import { setupTitleOnlyHeaderCell, setupGraphCell, setupSourceCell } from "../cellSetup";
import { GAP_PARA_FONT_SIZE_PT, GAP_PARA_SPACE_AFTER_PT } from "../../../core/config/constants";

/**
 * Inserts two stacked first-page exhibit tables separated by a collapsed gap paragraph.
 * Each table is a 3×1 structure identical to FullStrategy but using FP dimensions.
 */
export class FirstPageStrategy implements ExhibitInsertionStrategy {
  readonly layoutType = "first-page" as const;

  async execute(
    context: Word.RequestContext,
    anchorRange: Word.Range,
    config: LayoutConfig,
    profile: TemplateProfile,
    startingExhibitNumber: number
  ): Promise<Word.Table[]> {
    // ── TABLE 1 ───────────────────────────────────────────────────────────────
    const table1 = await this.insertSingleTable(
      context,
      anchorRange,
      config,
      profile
    );

    // ── Gap paragraph between the two tables ─────────────────────────────────
    // A collapsed paragraph is the only reliable way to create visual spacing
    // between two consecutive Word tables (Word has no table-margin property).
    const gapPara = table1.insertParagraph("", Word.InsertLocation.after);
    gapPara.font.size   = GAP_PARA_FONT_SIZE_PT;
    gapPara.spaceBefore = 0;
    gapPara.spaceAfter  = GAP_PARA_SPACE_AFTER_PT;
    gapPara.lineUnitBefore = 0;
    gapPara.lineUnitAfter  = 0;
    await context.sync();

    // ── TABLE 2 ───────────────────────────────────────────────────────────────
    const table2 = await this.insertSingleTable(
      context,
      gapPara,
      config,
      profile
    );

    return [table1, table2];
  }

  /**
   * Inserts one 3×1 exhibit table after anchorRange using the double-load pattern.
   * This is private — callers use execute().
   */
  private async insertSingleTable(
    context: Word.RequestContext,
    anchorRange: Word.Range | Word.Paragraph,
    config: LayoutConfig,
    profile: TemplateProfile
  ): Promise<Word.Table> {
    // ── 1. Insert table shell ────────────────────────────────────────────────
    const table = anchorRange.insertTable(3, 1, Word.InsertLocation.after);
    TableBuilder.clearAllBorders(table);
    TableBuilder.lockWidth(table, config.colWidth);
    TableBuilder.lockColumnWidth(table.getCell(0, 0), config.colWidth);

    // ── 2. FIRST load+sync — get row refs for height setting ─────────────────
    table.rows.load("items");
    await context.sync();

    const rowsForHeight = table.rows.items;
    rowsForHeight[0].setHeight(config.row1Height, Word.RowHeightRule.atLeast);
    rowsForHeight[1].setHeight(config.row2Height, Word.RowHeightRule.atLeast);
    rowsForHeight[2].setHeight(config.row3Height, Word.RowHeightRule.atLeast);

    // ── 3. SECOND load+sync — mandatory after height mutation ─────────────────
    table.rows.load("items");
    await context.sync();

    const rows = table.rows.items;

    // ── 4. Batch cell load ───────────────────────────────────────────────────
    rows[0].cells.load("items");
    rows[1].cells.load("items");
    rows[2].cells.load("items");
    await context.sync();

    // ── 5. Setup cells ───────────────────────────────────────────────────────
    await setupTitleOnlyHeaderCell(context, rows[0].cells.items[0], profile);
    await setupGraphCell(context, rows[1].cells.items[0], { height: config.row2Height, width: config.colWidth });
    await setupSourceCell(context, rows[2].cells.items[0], profile);

    return table;
  }
}